import {type FC, useId} from "react";
import {Badge, Box, Button, Flex, Progress, Text} from "@chakra-ui/react";
import {useCreditsStore} from "../../store/useCreditsStore.ts";
import {
  PLAN_NAMES,
  badgeStyles,
  currentSubscription,
  dateLineFor,
  formatCredits,
  isReadOnly,
  outlineButtonStyle,
  periodLabel,
  periodLeftPercent,
  planHeadingFor,
  planStatusFor,
  solidButtonStyle,
  statusBadgeStyle,
  tabularNums,
} from "./subscriptionPresentation.ts";

export {MemberNotice} from "./MemberNotice.tsx";
export {PlanSummaryLine} from "./PlanSummaryLine.tsx";

interface SubscriptionPanelProps {
  isSubscriptionActionLocked?: boolean;
  onChangePlan?: () => void;
  onCancelSubscription?: () => void;
  onResumeSubscription?: () => void;
}

const SubscriptionPanel: FC<SubscriptionPanelProps> = ({
  isSubscriptionActionLocked = false,
  onChangePlan,
  onCancelSubscription,
  onResumeSubscription,
}) => {
  const balance = useCreditsStore((state) => state.balance);
  const titleId = useId();
  // Pre-core balances (schemaVersion 1) carry none of the period fields this panel reads.
  if (balance?.schemaVersion !== 2) return null;

  const subscription = currentSubscription(balance);
  const status = planStatusFor(balance, subscription);
  const heading = planHeadingFor(balance, subscription);
  const detail = subscription
    ? dateLineFor(subscription)
    : balance.unlimited
      ? "Unlimited credits"
      : "Your credits don't expire. A monthly plan adds a renewing allowance.";
  // Change plan only leads to the package cards, where a member could act on nothing.
  const showChangePlan = !balance.unlimited && !isReadOnly(balance) && onChangePlan !== undefined;
  const showCancel = subscription !== null && balance.allowedActions.cancel && onCancelSubscription !== undefined;
  const showResume = subscription !== null && balance.allowedActions.resume && onResumeSubscription !== undefined;
  return (
    <Box as="section" aria-labelledby={titleId} p={5} rounded="2xl" bg="n.200" data-testid="subscription-panel">
      <Flex direction={{base: "column", md: "row"}} gap={{base: 4, md: 8}}>
        <Box flex="1" minW={0}>
          <Text
            id={titleId}
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="wider"
            textTransform="uppercase"
            color="n.400"
          >
            Current plan
          </Text>
          <Flex align="center" wrap="wrap" gap={2} mt={1}>
            <Text
              as="h3"
              className="text-balance"
              fontSize="xl"
              fontWeight="bold"
              color="n.700"
              data-testid="subscription-plan-name"
            >
              {heading}
            </Text>
            {status && (
              <Badge {...statusBadgeStyle} {...badgeStyles[status.tone]} data-testid="subscription-status">
                {status.label}
              </Badge>
            )}
          </Flex>
          <Text mt={1} fontSize="sm" color="n.500" sx={tabularNums} data-testid="subscription-date">{detail}</Text>
          {subscription?.nextPlanId && (
            <Text fontSize="sm" color="n.500" data-testid="subscription-next-plan">
              {`Next period: ${PLAN_NAMES[subscription.nextPlanId]}`}
            </Text>
          )}
          {(showChangePlan || showResume || showCancel) && (
            <Flex mt={4} gap={2} wrap="wrap" data-testid="subscription-actions">
              {showChangePlan && <Button {...solidButtonStyle} onClick={onChangePlan}>Change plan</Button>}
              {showResume && (
                <Button {...solidButtonStyle} isDisabled={isSubscriptionActionLocked} onClick={onResumeSubscription}>
                  Resume subscription
                </Button>
              )}
              {showCancel && (
                <Button {...outlineButtonStyle} isDisabled={isSubscriptionActionLocked} onClick={onCancelSubscription}>
                  Cancel subscription
                </Button>
              )}
            </Flex>
          )}
        </Box>
        {subscription && (
          <Box flex="1" minW={0} data-testid="subscription-period">
            <Flex justify="space-between" align="baseline" gap={3}>
              <Text fontSize="sm" color="n.500">Plan credits</Text>
              <Text fontSize="sm" fontWeight="semibold" color="n.700" sx={tabularNums}>{periodLabel(balance)}</Text>
            </Flex>
            <Progress
              mt={2}
              value={periodLeftPercent(balance)}
              size="sm"
              rounded="full"
              bg="n.300"
              sx={{"& > div": {bg: "brandPurple.500"}}}
              aria-label="Plan credits left this period"
            />
            <Text mt={2} fontSize="xs" color="n.400" sx={tabularNums}>
              {`${formatCredits(balance.planConsumedCredits)} used this period`}
            </Text>
          </Box>
        )}
        {!balance.unlimited && (
          <Box minW={{md: "10rem"}} textAlign={{md: "right"}} data-testid="subscription-non-plan">
            <Text fontSize="sm" color="n.500">Extra credits</Text>
            <Text fontSize="xl" fontWeight="bold" color="n.700" sx={tabularNums}>
              {formatCredits(balance.nonPlanCredits)}
            </Text>
            <Text fontSize="xs" color="n.400">Don't expire with the period</Text>
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default SubscriptionPanel;
